//! @ngInject
export function clockinClockoutLinkCallModalCtrl(
      $rootScope,
      $scope,
      $uibModalInstance,
      DatabaseApi,
      ClockinClockoutService,
      clockinClockoutRecord,
      SweetAlert,
      toaster
    ) {
      $scope.record = clockinClockoutRecord;
      $scope.possibilities = [];
      $scope.selectedPossiblity = null;
      $scope.isProcessing = false;
      $scope.invalidPhoneClockErrorMessage = "This clock record cannot be linked because the source phone number can't be assigned to the patient's profile";
      $scope.linkSourcePhoneModalState = {
        isOpen: false,
        isFetchingPatient: false,
        isLinking: false,
      }
      
      $scope.closestPossiblity = ($scope.record.potentialLinks || []).sort((a, b) => {
          return a.clockOffsetInHours < b.clockOffsetInHours ? -1 : 1;
      })[0]

      // ── Manual resolution (unknown caregiver / no suggested links) ──────────
      // When a call came in without an identified caregiver, the server has no
      // potential links to offer. The coordinator assigns a caregiver (and
      // optionally a patient), fetches that caregiver's nearby visits, picks
      // one, and selects the duties performed — all sent on link.
      $scope.needsCaregiver = $scope.record.caregiver.id === null;
      $scope.showManualResolution =
        $scope.needsCaregiver || ($scope.record.potentialLinks || []).length === 0;

      $scope.manual = {
        caregiverId: $scope.record.caregiver.id,
        patientId: $scope.record.patient.id,
        candidateVisits: [],
        selectedVisit: null,
        selectedDutyIds: [],
        isFetching: false,
        hasFetched: false
      };

      // Caregiver + patient search (typeahead). The caregiver-patient-search
      // directive filters these id-keyed maps client-side, so it's fast even
      // with many caregivers. Pre-select whatever the record already knows.
      // Pass the ids straight through - the picker resolves each against the
      // search index, so neither dictionary is loaded for this modal.
      $scope.preSelectedCaregiver = $scope.record.caregiver.id;
      $scope.preSelectedPatient = $scope.record.patient.id;

      $scope.onCaregiverSelect = (caregiver) => {
        $scope.manual.caregiverId = caregiver ? caregiver.id : null;
        refreshCandidateVisits();
      };
      $scope.onCaregiverDeselect = () => {
        $scope.manual.caregiverId = null;
        resetCandidateVisits();
      };
      $scope.onPatientSelect = (patient) => {
        $scope.manual.patientId = patient ? patient.id : null;
        refreshCandidateVisits();
      };
      $scope.onPatientDeselect = () => {
        $scope.manual.patientId = null;
        refreshCandidateVisits();
      };

      function resetCandidateVisits() {
        $scope.manual.candidateVisits = [];
        $scope.manual.selectedVisit = null;
        $scope.manual.selectedDutyIds = [];
        $scope.manual.hasFetched = false;
      }

      // Show visits for the chosen caregiver (+ patient) combination. A
      // caregiver is required; the patient further narrows the results.
      function refreshCandidateVisits() {
        if (!$scope.manual.caregiverId) {
          resetCandidateVisits();
          return;
        }
        $scope.fetchCandidateVisits();
      }

      $scope.fetchCandidateVisits = () => {
        if (!$scope.manual.caregiverId) {
          return;
        }
        $scope.manual.isFetching = true;
        $scope.manual.selectedVisit = null;
        $scope.manual.selectedDutyIds = [];

        ClockinClockoutService.getCandidateVisits($scope.record.id, {
          agencyId: $rootScope.agencyId,
          agencyMemberId: $rootScope.agencyMemberId,
          caregiverId: $scope.manual.caregiverId,
          patientId: $scope.manual.patientId || undefined
        })
          .then(({ data }) => {
            $scope.manual.candidateVisits = data.candidateVisits || [];
            $scope.manual.hasFetched = true;
          })
          .catch((error) => {
            console.error(error);
            toaster.pop("error", "Something went wrong", "Could not load candidate visits");
          })
          .finally(() => ($scope.manual.isFetching = false));
      };

      $scope.selectCandidateVisit = (visit) => {
        $scope.manual.selectedVisit = visit;
        // Default to none selected; coordinator checks the duties performed.
        $scope.manual.selectedDutyIds = [];
      };

      $scope.toggleDuty = (planOfCareItemId) => {
        const index = $scope.manual.selectedDutyIds.indexOf(planOfCareItemId);
        if (index === -1) {
          $scope.manual.selectedDutyIds.push(planOfCareItemId);
        } else {
          $scope.manual.selectedDutyIds.splice(index, 1);
        }
      };

      $scope.isDutySelected = (planOfCareItemId) =>
        $scope.manual.selectedDutyIds.indexOf(planOfCareItemId) !== -1;

      // A clock-OUT closes the visit, so where the visit's office requires
      // duties the coordinator has to say what was performed before linking.
      // (Nothing to require when the plan of care offers no duties.)
      $scope.mustSelectDutiesForVisit = (visit) =>
        $scope.record.callType === "OUT" &&
        visit !== null &&
        visit !== undefined &&
        visit.dutiesRequired === true &&
        (visit.duties || []).length > 0;

      $scope.canLinkManual = () =>
        $scope.manual.caregiverId !== null &&
        $scope.manual.caregiverId !== undefined &&
        $scope.manual.selectedVisit !== null &&
        (!$scope.mustSelectDutiesForVisit($scope.manual.selectedVisit) ||
          $scope.manual.selectedDutyIds.length > 0);

      $scope.linkManual = () => {
        if (!$scope.canLinkManual()) {
          return;
        }
        $scope.isProcessing = true;

        ClockinClockoutService.linkCalls($rootScope.agencyId, $rootScope.agencyMemberId, [
          {
            clockinClockoutRecordId: $scope.record.id,
            visitInstanceId: $scope.manual.selectedVisit.visitInstanceId,
            caregiverId: $scope.manual.caregiverId,
            dutyIds: $scope.manual.selectedDutyIds
          }
        ])
          .then(() => {
            toaster.pop("success", "Success", "Call has been successfully linked");
            $uibModalInstance.close({ type: "success" });
          })
          .catch((error) => {
            console.error(error);
            toaster.pop("error", "Something went wrong", "Could not link call");
          })
          .finally(() => ($scope.isProcessing = false));
      };

      // ── Recording playback ──────────────────────────────────────────────────
      $scope.recording = { isLoading: false, signedUrl: null };

      $scope.playRecording = () => {
        if ($scope.recording.signedUrl !== null) {
          window.open($scope.recording.signedUrl, "_blank");
          return;
        }
        $scope.recording.isLoading = true;

        ClockinClockoutService.getRecordingSignedUrl($scope.record.id, {
          agencyId: $rootScope.agencyId,
          agencyMemberId: $rootScope.agencyMemberId
        })
          .then(({ data }) => {
            if (data.signedUrl) {
              $scope.recording.signedUrl = data.signedUrl;
              window.open(data.signedUrl, "_blank");
            } else {
              toaster.pop("warning", "No recording", "This call has no stored recording");
            }
          })
          .catch((error) => {
            console.error(error);
            toaster.pop("error", "Something went wrong", "Could not load the recording");
          })
          .finally(() => ($scope.recording.isLoading = false));
      };

      // If the record already has a caregiver but no suggested links, show that
      // caregiver's candidate visits right away (the search boxes are pre-filled).
      if ($scope.showManualResolution && $scope.manual.caregiverId) {
        $scope.fetchCandidateVisits();
      }

      const canSourcePhoneBeAddedToPatient = possibility => {
        if (possibility.wasCallMadeFromThePatient || possibility.wasCallMadeFromTheCaregiver) {
          return false;
        }

        switch (clockinClockoutRecord.unlinkedReason) {
          case "MISSING_PATIENT":
          case "MISSING_VISIT":
          case "CLOCK_OUTSIDE_OF_RANGE":
          case "CLOCK_OUT_TOO_SOON":
          case "PHONE_WITHOUT_EVV_LOCATION":
          case "UNLINKED_MANUALLY":
          case "DUPLICATE_CALL":
            return true;
          case "CALL_FROM_CAREGIVER_PHONE":
          case "BLOCKED_CALLER_ID":
            return false;
          default:
            console.error(`Unhandled clockinClockoutRecord.unlinkedReason "${clockinClockoutRecord.status}"`);
            return false;
        }
      }

      // ── Duties on a clock-OUT link ──────────────────────────────────────────
      // Linking an OUT is what closes the visit, so when the visit's office
      // requires duties (the same office rule the IVR duty-requirement lambda
      // asks about) we ask for them here instead of silently linking without.
      $scope.outDuties = {
        isChecking: false,
        isOpen: false,
        possibility: null,
        duties: [],
        selectedDutyIds: []
      };

      // TODO(duty-occurrences): this picker can only ever record ONE occurrence
      // of a duty.
      //
      // A visit may perform the same duty several times -- `visit_caregiver_duty`
      // is keyed (visit, duty, occurrence) -- but the selection here is a set of
      // plan-of-care item ids, so a duty is either on the list or off it. The
      // payload is `dutyIds: PlanOfCareItemId[]` (see `linkParam.dutyIds` below
      // and `ClockinClockoutRecord.ts`), and the server writes every one of them
      // at occurrence 1 (`ClockinClockoutRecordCtrl.ts`).
      //
      // Note the LIST itself is safe to render: it comes from
      // `visit_instance_required_duty`, which is one row per (visit, duty), so
      // the `track by duty.planOfCareItemId` in the template cannot see a
      // duplicate. The limit is what a coordinator can SAY, not what is shown.
      //
      // Widening it is the same shape of change already made to `dutiesDone` and
      // to the caregiver duty-sheet answer: carry an occurrence per entry, and
      // scope the write to the (duty, occurrence) pairs the payload names instead
      // of assuming the first. Deliberately left out of the occurrences work --
      // FreedomCare's IVR flow was not in scope -- rather than overlooked.
      $scope.toggleOutDuty = (planOfCareItemId) => {
        const index = $scope.outDuties.selectedDutyIds.indexOf(planOfCareItemId);
        if (index === -1) {
          $scope.outDuties.selectedDutyIds.push(planOfCareItemId);
        } else {
          $scope.outDuties.selectedDutyIds.splice(index, 1);
        }
      };

      $scope.isOutDutySelected = (planOfCareItemId) =>
        $scope.outDuties.selectedDutyIds.indexOf(planOfCareItemId) !== -1;

      $scope.canConfirmOutDuties = () => $scope.outDuties.selectedDutyIds.length > 0;

      $scope.cancelOutDuties = () => {
        $scope.outDuties.isOpen = false;
        $scope.outDuties.possibility = null;
        $scope.outDuties.duties = [];
        $scope.outDuties.selectedDutyIds = [];
        $scope.selectedPossiblity = null;
      };

      // Duties chosen — carry on with the normal link confirmation.
      $scope.confirmOutDuties = () => {
        const possibility = $scope.outDuties.possibility;
        if (!$scope.canConfirmOutDuties() || possibility === null) {
          return;
        }
        $scope.outDuties.isOpen = false;
        continueSelectPossibility(possibility);
      };

      /**
       * Select a visit instance possibility the unlinked record
       */
      $scope.selectPossibility = possibility => {
        $scope.selectedPossiblity = possibility;

        if ($scope.record.callType !== "OUT") {
          return continueSelectPossibility(possibility);
        }

        // Ask the server whether this visit's office requires duties, and what
        // its plan of care offers, before letting the link through.
        $scope.outDuties.isChecking = true;

        return ClockinClockoutService.getVisitDutyRequirement($scope.record.id, {
          agencyId: $rootScope.agencyId,
          agencyMemberId: $rootScope.agencyMemberId,
          visitInstanceId: possibility.visitInstance.id
        })
          .then(({ data }) => {
            const duties = data.duties || [];

            // Nothing required, or nothing to pick from: link as before.
            if (data.dutiesRequired !== true || duties.length === 0) {
              return continueSelectPossibility(possibility);
            }

            $scope.outDuties.isOpen = true;
            $scope.outDuties.possibility = possibility;
            $scope.outDuties.duties = duties;
            $scope.outDuties.selectedDutyIds = [];
          })
          .catch((error) => {
            console.error(error);
            toaster.pop("error", "Something went wrong", "Could not check the duty requirement");
          })
          .finally(() => ($scope.outDuties.isChecking = false));
      };

      // The original select flow: source-phone checks, then "are you sure".
      function continueSelectPossibility(possibility) {
        if (!possibility.wasCallMadeFromThePatient) {
            return canSourcePhoneBeAddedToPatient(possibility)
                ? promptAddSourcePhoneToPatient(possibility)
                : promptInvalidAlert();
        }

        promptAlert(possibility);
      }

      $scope.handleClickOpenVisit = (e, possibility) => {
          e.stopPropagation();
          $rootScope.openVisitInstanceModal(possibility.visitInstance.id);
      }

      $scope.linkPhoneAndCall = async ({ possibility, address }) => {
        setSourcePhoneModalState(prev => ({ ...prev, view: "Loading", isLinking: true }));
  
        attachSourcePhoneToPatient({ patientId: possibility.patient.id, addressId: address.id })
          .then(() => {
            return linkCall(possibility)
              .then(() => setSourcePhoneModalState(prev => ({ ...prev, view: "Ready", isLinking: false })));
          })
          .catch(error => setSourcePhoneModalState(prev => ({...prev, view: "Error", error: error.message || "Failed to link phone and call" })))
      }

      function attachSourcePhoneToPatient({ patientId, addressId }) {
        const url = "agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/addresses/:patientAddressId/create_phonenumber"
          .replace(":agencyId", $rootScope.agencyId)
          .replace(":agencyMemberId", $rootScope.agencyMemberId)
          .replace(":patientId", patientId)
          .replace(":patientAddressId", addressId);

        const body = { phoneNumber: clockinClockoutRecord.sourcePhone };

        return DatabaseApi.post(url, body);
      }

      function promptAddSourcePhoneToPatient(possibility) {
        setSourcePhoneModalState({ view: "Loading", isOpen: true, isFetchingPatient: true, possibility });

        getPatient(possibility.patient.id)
          .then(({ data }) => setSourcePhoneModalState(prev => ({...prev, view: "Ready", patient: data })))
          .catch(error => setSourcePhoneModalState(prev => ({...prev, view: "Error", error: error.message || "Failed to fetch patient data" })))
          .then(() => setSourcePhoneModalState(prev => ({...prev, isFetchingPatient: false })));
      }

      function getPatient(patientId) {
        const url = `agencies/:agencyId/patients/:patientId`
            .replace(":agencyId", $rootScope.agencyId)
            .replace(":patientId", patientId);

        return DatabaseApi.get(url);
      }

      function setSourcePhoneModalState(dispatcher) {
        $scope.linkSourcePhoneModalState = typeof dispatcher === "function"
          ? dispatcher($scope.linkSourcePhoneModalState)
          : dispatcher;
      }

      function promptInvalidAlert() {
        SweetAlert.swal(
            {
              title: "Can't link call!",
              text: $scope.invalidPhoneClockErrorMessage,
              type: "error",
              showCancelButton: true,
              cancelButtonText: "Close",
              showConfirmButton: false,
            },
          );
      }

      /**
       * Prompt "Are you sure" alert for linking the record
       * @param {*} possibility 
       */
      function promptAlert(possibility) {
        SweetAlert.swal(
          {
            title: "Link Call",
            text: "Are you sure you want to link this call?",
            type: "info",
            showCancelButton: true,
            confirmButtonColor: "#3077EB",
            confirmButtonText: "Yes, link",
            closeOnConfirm: true,
            showConfirmButton: true
          },
          hasConfirmed => hasConfirmed && linkCall(possibility)
        );
      }

      /**
       * Send a PATCH request to link the record
       * @param {*} possibility 
       */
      function linkCall(possibility) {
        $scope.isProcessing = true;

        const linkParam = {
          clockinClockoutRecordId: possibility.id,
          visitInstanceId: possibility.visitInstance.id
        };

        // Duties picked for this OUT link travel with it.
        if (
          $scope.outDuties.possibility === possibility &&
          $scope.outDuties.selectedDutyIds.length > 0
        ) {
          linkParam.dutyIds = $scope.outDuties.selectedDutyIds;
        }

        return ClockinClockoutService.linkCalls($rootScope.agencyId, $rootScope.agencyMemberId,
          [linkParam])
          .then(() => onSuccessLink({ type: "success", singleLinkedCall: possibility}))
          .catch((error) => onFailLink({ type: "fail", error }))
          .finally(() => ($scope.isProcessing = false));
      }

      /**
       * Hanlde success response of linking a record
       * @param {Response} response 
       */
      function onSuccessLink(response) {
        toaster.pop("succes", "Success", "Call has been successfully linked");
        $uibModalInstance.close(response);
      }

      /**
       * Handle an error response of linking a record
       * @param {Error} error 
       */
      function onFailLink({ type: _type, error }) {
        console.error(error);
        toaster.pop("error", "Something went wrong", "Could not link call");
      }

      $scope.closeModal = () => {
        $uibModalInstance.close({ type: "close" });
      }

      
      $scope.canSourcePhoneBeAddedToPatient = canSourcePhoneBeAddedToPatient;
    };
